// Saved games — a browser-local working set for the Value Dashboard (#120).
// Presentation preference ONLY: nothing here reaches the relay, the org, or
// another device, and no price is ever persisted.
//
// ── Identity ────────────────────────────────────────────────────────────
// An entry keys on `gameMarkId(row.key)`, and rows that cannot express a game
// number fall back to `collapsedGameKey`. Both rules — and why a doubleheader
// must stay two entries while one game on two venues must collapse to one —
// live in `gameMarkIdentity`, shared with the minimized list (#654).
//
// Rows with no canonical game key (playoff-series rows carry a "9999-..."
// sentinel date) return null and simply aren't savable.
//
// ── Persistence ─────────────────────────────────────────────────────────
// One localStorage key per signed-in user, so a logout/login on a shared
// browser never shows the previous user's list — the same intent as
// authStore's purgeUserCaches(). The list itself is NOT cleared on logout;
// signing back in restores it.
//
// Writes are best-effort: a full or unavailable localStorage (private mode)
// degrades to in-memory only rather than throwing, matching displaySettings.

import { create } from 'zustand';
import { storageKey } from '@/lib/infra/storage';
import {
  collapsedGameKey,
  gameMarkId,
  isSportBearing,
  ownedBy,
  type MarkableGame,
} from './gameMarkIdentity';

export { collapsedGameKey, gameMarkId } from './gameMarkIdentity';

/** The minimum display metadata needed to still draw a row for a game that
 *  has fallen out of the live feed. Deliberately no prices, no status, no
 *  venue books — a stale snapshot must never render as a live quote. */
export interface SavedGame {
  /** Doubleheader-safe canonical id. See gameMarkId. */
  id: string;
  sport: string;
  date: string;
  awayCode: string;
  homeCode: string;
  /** Epoch ms, for stable ordering of the saved list. */
  savedAt: number;
}

/** The subset of a GameRow this store needs. Shared with the minimized list
 *  — a game is identified the same way whichever list is marking it. */
export type SavableGame = MarkableGame;

/** Stable empty result so a non-owning read doesn't mint a new array each
 *  render and defeat zustand's reference equality. */
const NO_GAMES: SavedGame[] = [];

/**
 * The saved list, but only when it belongs to `viewerId`. Any mismatch — a
 * logout not yet reset, or a new user's first paint before hydration — yields
 * an empty list rather than the previous account's games.
 */
export function selectOwnedGames(
  state: { userId: string | null; games: SavedGame[] },
  viewerId: string | null,
): SavedGame[] {
  return ownedBy(state, viewerId) ? state.games : NO_GAMES;
}

/**
 * Clear the in-memory saved list. Persisted lists are deliberately left intact,
 * so signing back in restores your own games.
 *
 * Called from authStore.purgeUserCaches, which runs on EVERY path to
 * unauthenticated — including the cross-tab storage handler. That is the fix
 * for the lifecycle hole: the ValueDashboard effect cannot be the only reset,
 * because logout unmounts that page, potentially before the effect ever runs
 * with a null user.
 */
export function resetSavedGames(): void {
  useSavedGames.getState().hydrateFor(null);
}

/**
 * True when `rowKey` refers to a game the user has saved.
 *
 * Two matching rules, because the dashboard's rows don't all carry the same
 * identity precision:
 *
 *  - A row that carries a sport (every live row, including one that has just
 *    gone FINAL while still in the feed) matches on the EXACT doubleheader-safe
 *    id. This is what keeps an MLB doubleheader's game 1 and game 2 independent.
 *
 *  - A row projected from the analytics history is sport-less and has no game
 *    number — that surface has no doubleheader concept at all — so it can only
 *    be matched on the collapsed key. A doubleheader therefore surfaces ONE
 *    archived row, which reads as saved if EITHER game was saved (and un-saving
 *    there clears both, because that single row represents both).
 *
 * The asymmetry is deliberate: precision is preserved everywhere it exists, and
 * degrades only on the rows that genuinely cannot express it. Without this, a
 * game you saved would appear un-saved the moment hydra pruned it from the live
 * feed and it came back from analytics instead.
 */
export function isRowSaved(
  rowKey: string | null | undefined,
  state: {
    userId: string | null;
    ids: ReadonlySet<string>;
    collapsedIds: ReadonlySet<string>;
  },
  viewerId: string | null,
): boolean {
  // Ownership first: nothing about another account's list may reach the screen,
  // whatever the key says.
  if (!ownedBy(state, viewerId)) return false;
  if (isSportBearing(rowKey)) {
    const id = gameMarkId(rowKey);
    return id != null && state.ids.has(id);
  }
  const collapsed = collapsedGameKey(rowKey);
  return collapsed != null && state.collapsedIds.has(collapsed);
}

function storageKeyFor(userId: string): string {
  return storageKey(`galactus:savedGames:${userId}`);
}

function load(userId: string): SavedGame[] {
  try {
    const raw = localStorage.getItem(storageKeyFor(userId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Drop anything malformed rather than letting one bad entry poison the
    // whole list — a saved game the user can't see is also one they can't
    // remove.
    return parsed.filter(isSavedGame);
  } catch {
    // Unavailable / malformed localStorage — start empty.
    return [];
  }
}

function isSavedGame(v: unknown): v is SavedGame {
  if (!v || typeof v !== 'object') return false;
  const g = v as Record<string, unknown>;
  return (
    typeof g.id === 'string' && g.id !== '' &&
    typeof g.sport === 'string' &&
    typeof g.date === 'string' &&
    typeof g.awayCode === 'string' &&
    typeof g.homeCode === 'string' &&
    typeof g.savedAt === 'number'
  );
}

function persist(userId: string, games: SavedGame[]): void {
  try {
    localStorage.setItem(storageKeyFor(userId), JSON.stringify(games));
  } catch {
    // localStorage full or unavailable — in-memory state still works for
    // this session. Silent, same as displaySettings.
  }
}

const EMPTY_IDS: ReadonlySet<string> = new Set<string>();

/** Both membership indexes for a list. Rebuilt on every mutation so zustand
 *  subscribers re-render. */
function indexOf(games: SavedGame[]): {
  ids: ReadonlySet<string>;
  collapsedIds: ReadonlySet<string>;
} {
  const ids = new Set<string>();
  const collapsedIds = new Set<string>();
  for (const g of games) {
    ids.add(g.id);
    const c = collapsedGameKey(g.id);
    if (c) collapsedIds.add(c);
  }
  return { ids, collapsedIds };
}

interface SavedGamesState {
  /** Whose list is currently loaded. Null when signed out. */
  userId: string | null;
  games: SavedGame[];
  /** Exact-id membership — the doubleheader-safe lookup. */
  ids: ReadonlySet<string>;
  /** Collapsed-key membership, for rows that can't express a game number. */
  collapsedIds: ReadonlySet<string>;
  /** Point the store at a user's list. Call whenever the signed-in user
   *  changes; passing null clears the in-memory list (localStorage is left
   *  intact so signing back in restores it). */
  hydrateFor: (userId: string | null) => void;
  /** Save the game if it isn't saved, un-save it if it is. `viewerId` is the
   *  signed-in user the action is on behalf of; a mismatch against the loaded
   *  list's owner is a no-op, so one account's click can never land in
   *  another's saved games. No-op too when the row has no canonical key. */
  toggle: (game: SavableGame, viewerId: string | null) => void;
  /** Un-save by exact stored id — the removal path for a saved game with no
   *  row on the board to toggle against. Same ownership rule as toggle. */
  remove: (id: string, viewerId: string | null) => void;
}

export const useSavedGames = create<SavedGamesState>((set, get) => ({
  userId: null,
  games: [],
  ids: EMPTY_IDS,
  collapsedIds: EMPTY_IDS,

  hydrateFor: (userId) => {
    if (get().userId === userId) return;
    if (!userId) {
      set({ userId: null, games: [], ids: EMPTY_IDS, collapsedIds: EMPTY_IDS });
      return;
    }
    const games = load(userId);
    set({ userId, games, ...indexOf(games) });
  },

  toggle: (game, viewerId) => {
    const state = get();
    const { userId, games } = state;
    if (!userId || !ownedBy(state, viewerId)) return;

    let next: SavedGame[];

    if (isSportBearing(game.key)) {
      const id = gameMarkId(game.key);
      if (!id) return;
      next = games.some((g) => g.id === id)
        ? games.filter((g) => g.id !== id)
        : [...games, entryFor(id, game)];
    } else {
      // Sport-less row (analytics archived). It can't name a specific game of
      // a doubleheader, so it un-saves every entry it could be referring to —
      // this one row IS both of them on screen.
      const collapsed = collapsedGameKey(game.key);
      if (!collapsed) return;
      const alreadySaved = games.some((g) => collapsedGameKey(g.id) === collapsed);
      next = alreadySaved
        ? games.filter((g) => collapsedGameKey(g.id) !== collapsed)
        : [...games, entryFor(collapsed, game)];
    }

    commit(set, userId, next);
  },

  remove: (id, viewerId) => {
    const state = get();
    const { userId, games } = state;
    if (!userId || !ownedBy(state, viewerId)) return;
    if (!games.some((g) => g.id === id)) return;
    commit(set, userId, games.filter((g) => g.id !== id));
  },
}));

function entryFor(id: string, game: SavableGame): SavedGame {
  return {
    id,
    sport: game.sport,
    date: game.date,
    awayCode: game.awayCode,
    homeCode: game.homeCode,
    savedAt: Date.now(),
  };
}

function commit(
  set: (partial: Partial<SavedGamesState>) => void,
  userId: string,
  next: SavedGame[],
): void {
  set({ games: next, ...indexOf(next) });
  persist(userId, next);
}
